import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { ScanLine, CheckCircle2, AlertTriangle, Volume2, VolumeX, RotateCcw } from 'lucide-react';

// ---- Audio feedback (Web Audio API) ----
let audioCtx = null;
function getAudioCtx() {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}
function tone({ freq = 880, duration = 0.1, type = 'triangle', gain = 0.18, delay = 0 }) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
}
const sounds = {
  success: () => {
    tone({ freq: 880, duration: 0.08, gain: 0.18 });
    tone({ freq: 1320, duration: 0.12, gain: 0.18, delay: 0.07 });
  },
  warn: () => {
    tone({ freq: 260, duration: 0.18, type: 'square', gain: 0.12 });
  },
  celebrate: () => {
    tone({ freq: 660, duration: 0.09, gain: 0.2 });
    tone({ freq: 880, duration: 0.09, gain: 0.2, delay: 0.09 });
    tone({ freq: 1320, duration: 0.2, gain: 0.22, delay: 0.18 });
  },
};

const DEDUPE_MS = 2000;
const SOUND_LS_KEY = 'wgi_scan_sound';

export default forwardRef(function ScanInput(
  {
    direction = 'pack', // 'pack' | 'return'
    onScan, // (code, directionOverride?) => result
    isComplete = false,
  },
  ref,
) {
  const inputRef = useRef(null);
  const lastScanRef = useRef({ code: '', ts: 0 });
  const wasCompleteRef = useRef(isComplete);

  const [code, setCode] = useState('');
  const [recent, setRecent] = useState([]); // last 5
  const [flash, setFlash] = useState(null); // 'ok' | 'warn' | null (brief highlight)
  const [focused, setFocused] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const v = localStorage.getItem(SOUND_LS_KEY);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SOUND_LS_KEY, soundOn ? '1' : '0');
    } catch {
      // ignore
    }
  }, [soundOn]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Celebrate audio when transitioning to complete
  useEffect(() => {
    if (isComplete && !wasCompleteRef.current && soundOn) {
      sounds.celebrate();
    }
    wasCompleteRef.current = isComplete;
  }, [isComplete, soundOn]);

  const submit = async (raw, directionOverride = null, opts = {}) => {
    const value = (raw || '').trim();
    if (!value) return;
    const effectiveDir = directionOverride || direction;
    const now = Date.now();
    if (
      !opts.skipDedupe &&
      lastScanRef.current.code === value &&
      now - lastScanRef.current.ts < DEDUPE_MS
    ) {
      setCode('');
      inputRef.current?.focus();
      return; // silently ignore rapid duplicate
    }
    lastScanRef.current = { code: value, ts: now };

    let result;
    try {
      result = onScan ? await onScan(value, effectiveDir) : { ok: false, error: 'No handler' };
    } catch (e) {
      result = { ok: false, error: e?.message || 'Scan failed' };
    }

    let message = '';
    if (result.ok) {
      if (result.kit) {
        const okCount = (result.results || []).filter((r) => r.ok).length;
        const total = result.kit.itemCodes.length;
        message =
          effectiveDir === 'pack'
            ? `Kit ${result.kit.code} — packed ${okCount} / ${total}`
            : `Kit ${result.kit.code} — returned ${okCount} / ${total}`;
      } else if (result.item) {
        message =
          effectiveDir === 'pack'
            ? `Packed ${result.item.name}`
            : `Returned ${result.item.name}`;
      } else {
        message = result.message || (effectiveDir === 'pack' ? 'Packed' : 'Returned');
      }
      if (soundOn) sounds.success();
      setFlash('ok');
    } else {
      message = result.error || 'Could not process scan.';
      if (soundOn) sounds.warn();
      setFlash('warn');
    }

    setRecent((prev) =>
      [
        {
          id: `${now}_${Math.random().toString(36).slice(2, 6)}`,
          code: value,
          ok: !!result.ok,
          message,
          ts: now,
          source: opts.source || 'scan',
        },
        ...prev,
      ].slice(0, 5),
    );
    setCode('');
    setTimeout(() => setFlash(null), 600);
    // Re-focus so next BT scan flows in immediately
    inputRef.current?.focus();
  };

  // Expose imperative API so manual clicks elsewhere can route through here
  useImperativeHandle(ref, () => ({
    submitCode: (code, directionOverride = null) =>
      submit(code, directionOverride, { skipDedupe: true, source: 'manual' }),
  }));

  const isPack = direction === 'pack';
  const accent = isPack
    ? { ring: 'amber-500', text: 'amber-300', bg: 'amber-500/10', border: 'amber-500/30' }
    : { ring: 'emerald-500', text: 'emerald-300', bg: 'emerald-500/10', border: 'emerald-500/30' };

  return (
    <div
      className={`surface rounded-lg p-4 relative overflow-hidden transition-colors ${
        flash === 'ok'
          ? 'border-emerald-500/60'
          : flash === 'warn'
          ? 'border-amber-500/60'
          : ''
      }`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              focused ? 'bg-emerald-400' : 'bg-neutral-600'
            }`}
          >
            {focused && (
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
            )}
          </span>
          <span className="text-[11px] tracking-wider uppercase font-medium text-white">
            {focused ? 'Scanner Ready' : 'Scanner Paused'}
          </span>
          <span className="text-[10px] text-neutral-500">
            · {isPack ? 'Pack mode' : 'Return mode'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!focused && (
            <button
              onClick={() => inputRef.current?.focus()}
              className="text-[10px] px-2 py-1 rounded border border-neutral-800 text-neutral-300 hover:bg-neutral-900 flex items-center gap-1"
            >
              <RotateCcw size={10} /> Resume
            </button>
          )}
          <button
            onClick={() => setSoundOn((v) => !v)}
            title={soundOn ? 'Mute sounds' : 'Enable sounds'}
            className="h-6 w-6 grid place-items-center rounded border border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-900"
          >
            {soundOn ? <Volume2 size={11} /> : <VolumeX size={11} />}
          </button>
        </div>
      </div>

      {/* Big scan field */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(code);
        }}
        className="relative"
      >
        <ScanLine
          size={18}
          className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${
            isPack ? 'text-amber-400' : 'text-emerald-400'
          }`}
        />
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={
            isPack
              ? 'Scan or type a code to pack (WC0002, KIT001…)'
              : 'Scan or type a code to check in (WC0002, KIT001…)'
          }
          autoComplete="off"
          spellCheck="false"
          className={`w-full bg-neutral-950 border-2 text-white text-base rounded-md pl-11 pr-24 py-3.5 focus:outline-none font-mono tracking-wide transition-colors ${
            focused
              ? isPack
                ? 'border-amber-500/60 ring-2 ring-amber-500/20'
                : 'border-emerald-500/60 ring-2 ring-emerald-500/20'
              : 'border-neutral-800'
          }`}
        />
        <button
          type="submit"
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-xs px-4 py-2 rounded-md font-semibold ${
            isPack ? 'btn-amber' : 'bg-emerald-500 text-neutral-950 hover:bg-emerald-400 transition-colors'
          }`}
        >
          {isPack ? 'Pack' : 'Check In'}
        </button>
      </form>

      <p className="text-[10px] text-neutral-500 mt-2">
        Tip: pair a Bluetooth QR scanner (HID/keyboard mode) and pull the trigger — codes will type in
        and submit automatically. Kit scans cascade to every item inside.
      </p>

      {/* Recent scans inline */}
      {recent.length > 0 && (
        <div className="mt-3 pt-3 border-t border-neutral-900">
          <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1.5">Recent</div>
          <div className="space-y-1">
            {recent.map((r) => (
              <div
                key={r.id}
                className="flex items-start gap-2 text-[12px]"
              >
                {r.ok ? (
                  <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle size={12} className="text-amber-400 shrink-0 mt-0.5" />
                )}
                <span className="font-mono text-neutral-500 w-20 shrink-0">{r.code}</span>
                <span className={r.ok ? 'text-neutral-200' : 'text-amber-200/80'}>{r.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
